import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import {
  BanLookupRequest,
  BanLookupUpdateResponse,
} from 'src/app/models/ban-lookup.model';
import { Service } from 'src/app/models/service.model';
import * as fromStore from 'src/app/store/';
import { BanLookupService } from '../../services/ban-lookup.service';

interface BanLookupDialog {
  locationAddress: string;
  banLookupId: string;
  customerLocationAlias: string;
  services: Service[];
}

@Component({
  selector: 'app-location-name',
  templateUrl: './location-name.component.html',
  styleUrls: ['./location-name.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LocationNameComponent implements OnInit {
  dialogWidth: string;

  dialogHeight: string;

  readonly hasError = signal(false);

  readonly isSaving = signal(false);

  private fb = inject(UntypedFormBuilder);

  locationNameFormGroup: UntypedFormGroup = this.fb.group({
    banLookupId: this.fb.control('', Validators.required),
    locationNameAlias: this.fb.control('', Validators.required),
  });

  private store = inject<Store<fromStore.State>>(Store);

  private readonly dialogRef = inject(MatDialogRef<LocationNameComponent>);

  private banLookupService = inject(BanLookupService);

  readonly dialogData = inject<BanLookupDialog>(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.locationNameFormGroup.controls.banLookupId.setValue(
      this.dialogData.banLookupId,
    );
    this.locationNameFormGroup.controls.locationNameAlias.setValue(
      this.dialogData.customerLocationAlias,
    );
  }

  deleteLocationAlias(): void {
    const banLookupId = this.locationNameFormGroup.value.banLookupId as string;
    this.hasError.set(false);
    this.isSaving.set(true);

    // for cache / state management
    const updatedServices = this.dialogData.services.slice().map((s) => {
      const newServices = { ...s };
      if (newServices.BanLookupId === banLookupId) {
        newServices.CustomerLocationAlias = '';
      }
      return newServices;
    });

    this.banLookupService
      .deleteLocationAlias(banLookupId)
      .pipe(take(1))
      .subscribe({
        next: (result: BanLookupUpdateResponse[]) => {
          if (!result[0]?.success) {
            this.hasError.set(true);
            this.isSaving.set(false);
            return;
          }

          this.store.dispatch(
            new fromStore.UpdateServicesBanLookup(updatedServices),
          );
          this.dialogRef.close();
        },
        error: () => {
          this.hasError.set(true);
          this.isSaving.set(false);
        },
      });
  }

  saveLocationName(): void {
    const request: BanLookupRequest = {
      Id: this.locationNameFormGroup.value.banLookupId as string,
      Customer_Location_Alias__c: this.locationNameFormGroup.value
        .locationNameAlias as string,
    };
    this.updateBanLookup(request);
  }

  private updateBanLookup(value: BanLookupRequest): void {
    this.hasError.set(false);
    this.isSaving.set(true);

    // for cache / state management
    const updatedServices = this.dialogData.services.slice().map((s) => {
      const newServices = { ...s };
      if (newServices.BanLookupId === value.Id) {
        newServices.CustomerLocationAlias = value.Customer_Location_Alias__c;
      }
      return newServices;
    });

    this.banLookupService
      .updateBanLookup(value)
      .pipe(take(1))
      .subscribe({
        next: (result: BanLookupUpdateResponse[]) => {
          if (!result[0]?.success) {
            this.hasError.set(true);
            this.isSaving.set(false);
            return;
          }

          this.store.dispatch(
            new fromStore.UpdateServicesBanLookup(updatedServices),
          );
          this.dialogRef.close();
        },
        error: () => {
          this.hasError.set(true);
          this.isSaving.set(false);
        },
      });
  }
}
